<script setup lang="ts">
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
    faBadgeCheck,
    faUser,
    faCheck,
} from '@fortawesome/pro-regular-svg-icons';
import { type UserCredential, UserType } from '@store/auth';

const emit = defineEmits<{
    close: [];
}>();
const authStore = useAuthStore();
const { credentials, selectedCredentialIndex, userInfo } =
    classToRefs(authStore);

const credentialsExtended = computed<
    (UserCredential & {
        icon?: IconDefinition;
        selected?: boolean;
    })[]
>(() =>
    (credentials.value || []).map((credential, index) => ({
        ...credential,
        icon: getCredentialIcon(credential),
        selected: index === selectedCredentialIndex.value,
        title: getCredentialTitle(credential),
    }))
);
const getCredentialIcon = (credential: UserCredential) => {
    switch (credential.type) {
        case UserType.Organization:
            return faBadgeCheck;
        case UserType.User:
            return faUser;
        default:
            return undefined;
    }
};
const getCredentialTitle = (credential: UserCredential) => {
    switch (credential.type) {
        case UserType.User:
            return credential.title;
        case UserType.Organization:
            return `شرکت ${credential.title}`;
        default:
            return '';
    }
};
const submit = (index: number) => {
    authStore.setSelectedCredentialIndex(index);
    authStore.loadUser(true).then(() => {
        window.WebAppAuthChannel?.postMessage(
            userInfo.value?.userGroup?.toString() ?? ''
        );
    });
    useLayoutStore().autoReserveInProcessCount = 0;
    emit('close');
};
</script>
<template>
    <mr-modal title="تغییر حساب کاربری" width="auto" @close="emit('close')">
        <div class="login-popup-step">
            <div class="login-popup-text">
                حساب کاربری مورد نظر خود را انتخاب کنید.
            </div>
            <div class="divider" />
            <template
                v-for="(credential, index) in credentialsExtended"
                :key="index"
            >
                <div class="user-container" @click="submit(index)">
                    <mr-icon
                        v-if="credential.icon"
                        size="large"
                        :class="[
                            {
                                'organization-icon':
                                    credential.type === UserType.Organization,
                            },
                            'user-icon',
                        ]"
                        :icon="credential.icon"
                        :color="
                            credential.type === UserType.Organization
                                ? 'white'
                                : 'var(--blue-400)'
                        "
                    />
                    <mr-typography weight="700" color="var(--gray-900)" grow>
                        {{ credential.title }}
                    </mr-typography>
                    <mr-icon
                        v-show="credential.selected"
                        color="var(--blue-400)"
                        :icon="faCheck"
                    />
                </div>
                <div class="divider" />
            </template>
        </div>
    </mr-modal>
</template>

<style lang="scss" scoped>
.login-popup-step {
    width: 360px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    .login-popup-text {
        @include tg-m($w5);
        color: usePalette($p-gray, $p-900);
    }
    .divider {
        border: 1px solid usePalette($p-gray, $p-200);
        &:last-child {
            display: none;
        }
    }

    .user-container {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        cursor: pointer;
        .user-title {
            flex-grow: 1;
            @include tg-m($w7);
            color: usePalette($p-gray, $p-900);
        }
        .user-icon {
            width: 48px;
            height: 48px;
            border-radius: 99999px;
            background-color: usePalette($p-gray, $p-200) !important;
            &.organization-icon {
                background-color: usePalette($p-green, $p-400) !important;
            }
        }
    }
}
</style>
